<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'CreateMeterReadings',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    items: {
      type: Array,
      required: true,
      default: null,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const notes = ref(null);
  const irentWriteback = ref(false);
  const skipMetersNotFound = ref(false);
  const irentWritebackSkipMetersNotFound = ref(false);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));

  const $data = computed(() => ({
    notes: notes.value,
    irentWriteback: irentWriteback.value,
    skipMetersNotFound: skipMetersNotFound.value,
    irentWritebackSkipMetersNotFound: irentWritebackSkipMetersNotFound.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'notes' },
      { name: 'irentWriteback' },
      { name: 'skipMetersNotFound' },
      { name: 'irentWritebackSkipMetersNotFound' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {},
    ux: {},
    dataRefs: {
      notes,
      irentWriteback,
      skipMetersNotFound,
      irentWritebackSkipMetersNotFound,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext('CreateMeterReadings', 'Create Meter Readings'),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.items,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('items');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_3mpxjyh6q_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_register_gumlriypa_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'resultLength',
      value: $event.data.response.data ? $event.data.response.data.length : 0,
    };
  };
  const act_1040167445267876_notification_7y84wt79v_getActionArgs = async (
    $event
  ) => {
    return {
      message: $fn.value.ngettext(
        'One meter reading created.',
        '${n} meter readings created.',
        $event.data.resultLength
      ),
    };
  };
  const act_1040167445267876_notification_7y84wt79v_getWhen = async (
    $event
  ) => {
    return $event.data.resultLength > 0;
  };
  const act_1040167445267876_closeModal_anmmzes32_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        value: $event.data.response.data,
      },
    };
  };
  const act_1040167445267876_reloadSlickgrid_sgmvqlgyd_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'all',
    };
  };
  const act_1040167445267876_request_uhyz2zsno_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'create_meter_reading',
      data: props.items
        .filter((p) => !!p.meter_id)
        .map((p) => ({
          meter_id: p.meter_id,
          value: p.value,
          unit: p.unit,
          notes: notes.value,
          reading_type: 'automated',
          reading_date: $fn.value.formatDate(
            $fn.value.parseISODate(p.timestamp),
            'yyyy-MM-dd'
          ),
          reading_time: $fn.value.formatDate(
            $fn.value.parseISODate(p.timestamp),
            'hh:mm'
          ),
        })),
      parameters: {
        'irent-writeback': irentWriteback.value,
        'skip-meters-not-found': skipMetersNotFound.value,
        'irent-writeback-skip-meters-not-found':
          irentWritebackSkipMetersNotFound.value,
      },
    };
  };

  const act_7315092382398562_closeModal_3mpxjyh6q =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_3mpxjyh6q_getActionArgs,
    });
  const inst_evh_7315092382398562_close_nh80voukp =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_3mpxjyh6q,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_nh80voukp],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_ouzn9vpms =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_m1j6cur6f =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_ouzn9vpms,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_m1j6cur6f],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_m9ix1bk1g =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_gxow2hcso =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_m9ix1bk1g,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_gxow2hcso],
    event: 'close',
  });
  const act_1040167445267876_register_gumlriypa =
    new actionTypes.RegisterAction({
      actionArgs: act_1040167445267876_register_gumlriypa_getActionArgs,
    });
  const inst_evh_1040167445267876_success_m4pbb9pb5 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_register_gumlriypa,
      event: 'success',
      displayName: 'register',
    });
  const act_1040167445267876_notification_7y84wt79v =
    new actionTypes.NotificationAction({
      actionArgs: act_1040167445267876_notification_7y84wt79v_getActionArgs,
      when: act_1040167445267876_notification_7y84wt79v_getWhen,
    });
  const inst_evh_1040167445267876_success_xdzgdqi4x =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_notification_7y84wt79v,
      event: 'success',
      displayName: 'notification',
    });
  const act_1040167445267876_closeModal_anmmzes32 =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_anmmzes32_getActionArgs,
    });
  const inst_evh_1040167445267876_success_fqqkyo18w =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_anmmzes32,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_reloadSlickgrid_sgmvqlgyd =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_1040167445267876_reloadSlickgrid_sgmvqlgyd_getActionArgs,
    });
  const inst_evh_1040167445267876_success_hrxv38oat =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_reloadSlickgrid_sgmvqlgyd,
      event: 'success',
      displayName: 'reloadSlickgrid',
    });
  const act_1040167445267876_request_uhyz2zsno = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_uhyz2zsno_getActionArgs,
    displayName: 'submit',
    events: [
      inst_evh_1040167445267876_success_m4pbb9pb5,
      inst_evh_1040167445267876_success_xdzgdqi4x,
      inst_evh_1040167445267876_success_fqqkyo18w,
      inst_evh_1040167445267876_success_hrxv38oat,
    ],
  });
  const inst_evh_1040167445267876_submit_z89u793ts =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_uhyz2zsno,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_z89u793ts],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_v2krc5uvl =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_hvs7lftft =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_v2krc5uvl,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_hvs7lftft],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_nh80voukp,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_m1j6cur6f,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_gxow2hcso,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_m4pbb9pb5,
    inst_evh_1040167445267876_success_xdzgdqi4x,
    inst_evh_1040167445267876_success_fqqkyo18w,
    inst_evh_1040167445267876_success_hrxv38oat,
    inst_evh_1040167445267876_submit_z89u793ts,
    submit: inst_evh_1040167445267876_submit_z89u793ts,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_hvs7lftft,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_nh80voukp = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_nh80voukp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_m1j6cur6f = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_m1j6cur6f.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_gxow2hcso = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_gxow2hcso.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_m4pbb9pb5 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_m4pbb9pb5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_xdzgdqi4x = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_xdzgdqi4x.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_fqqkyo18w = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_fqqkyo18w.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_hrxv38oat = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_hrxv38oat.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_z89u793ts = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_z89u793ts.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_hvs7lftft = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_hvs7lftft.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="CreateMeterReadings"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        name="createOptions"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpFormGroup
          name="notes"
          :label="pgettext('CreateMeterReadings', 'Notes')"
        >
          <ItpFormTextarea
            name="notes"
            v-model="notes"
            :value="notes"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (notes =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
          >
          </ItpFormTextarea>
        </ItpFormGroup>
        <ItpFormGroup
          name="irentWriteback"
          :description="
            pgettext(
              'CreateMeterReadings',
              'Save values to the legacy database, too.'
            )
          "
          row
          :label-cols-md="0"
          :content-cols-md="12"
        >
          <ItpFormCheckbox
            name="irentWriteback"
            v-model="irentWriteback"
            :value="irentWriteback"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (irentWriteback =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :label="pgettext('CreateMeterReadings', 'IntegroRent writeback')"
          >
          </ItpFormCheckbox>
        </ItpFormGroup>
        <ItpFormGroup
          name="irentWriteback"
          :description="
            pgettext(
              'GenerateMeterReading',
              'Skip writeback to the legacy database if the serial number is not found in the legacy database.'
            )
          "
          row
          :label-cols-md="0"
          :content-cols-md="12"
        >
          <ItpFormCheckbox
            name="irentWritebackSkipMetersNotFound"
            v-model="irentWritebackSkipMetersNotFound"
            :value="irentWritebackSkipMetersNotFound"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (irentWritebackSkipMetersNotFound =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :label="
              pgettext(
                'CreateMeterReadings',
                'IntegroRent writeback - skip not found'
              )
            "
          >
          </ItpFormCheckbox>
        </ItpFormGroup>
        <ItpFormGroup
          name="skipMetersNotFound"
          :description="
            pgettext(
              'CreateMeterReadings',
              'Skip meters not found in the database.'
            )
          "
          row
          :label-cols-md="0"
          :content-cols-md="12"
        >
          <ItpFormCheckbox
            name="skipMetersNotFound"
            v-model="skipMetersNotFound"
            :value="skipMetersNotFound"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (skipMetersNotFound =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :label="pgettext('CreateMeterReadings', 'Skip not found')"
          >
          </ItpFormCheckbox>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :form="forms.createOptions && forms.createOptions.id"
          :text="pgettext('MeterMeasurementGenerateButton', 'Create Readings')"
          type="submit"
          variant="primary"
          :spinning="actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('CreateMeterReadings', 'Cancel')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
